import { useEffect, useState } from "react";
import { Clock3, Plus, Search, Wrench } from "lucide-react";
import { api, type Maintenance, type MaintenanceKanbanTemplate } from "./api";
import { KanbanBoard, type KanbanColumn } from "./kanban-board";
import { formatReportUppercaseText } from "./text-normalization";
import { MultiSelectFilter, TableDisplayControls, useTablePreferences } from "./table-controls";
import { leadTimeTone, maintenanceLeadTimeDays } from "./lead-time";
import { resolveKanbanModelColumn } from "./kanban-model";

const maintenanceStatusAliases: Record<string, string[]> = {
  submitted: ["in_analysis"],
  waiting_customer: ["in_progress"],
  waiting_parts: ["in_progress"],
  reopened: ["in_progress"],
};
const priorityLabels: Record<string, string> = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  critical: "Crítica",
};
const warrantyLabels: Record<string, string> = {
  analysis: "Em análise",
  in_warranty: "Em garantia",
  out_of_warranty: "Fora da garantia",
  approved_out_of_warranty: "Atendimento aprovado",
};
const columns = [
  { key: "code", label: "Código", required: true },
  { key: "customer", label: "Cliente" },
  { key: "description", label: "Descrição" },
  { key: "items", label: "Itens" },
  { key: "status", label: "Status" },
  { key: "priority", label: "Prioridade" },
  { key: "warranty", label: "Garantia" },
];

export function MaintenanceList({
  onError,
  onOpen,
  onAdd,
}: {
  onError: (message: string) => void;
  onOpen: (id: string) => void;
  onAdd: () => void;
}) {
  const [rows, setRows] = useState<Maintenance[]>([]),
    [query, setQuery] = useState(""),
    [statuses, setStatuses] = useState<string[]>([]),
    [priorities, setPriorities] = useState<string[]>([]),
    [warranties, setWarranties] = useState<string[]>([]),
    [page, setPage] = useState(1),
    [templates,setTemplates]=useState<MaintenanceKanbanTemplate[]>([]),[templateId,setTemplateId]=useState("");
  const preferences = useTablePreferences("sgq:maintenance-table", columns);
  useEffect(() => {
    Promise.all([api.list(),api.maintenanceKanbanTemplates()])
      .then(([items,nextTemplates])=>{setRows(items);setTemplates(nextTemplates);setTemplateId(current=>current&&nextTemplates.some(template=>template.id===current)?current:nextTemplates[0]?.id||"")})
      .catch((error) => onError(error.message));
  }, [onError]);
  useEffect(()=>setPage(1),[query,statuses,priorities,warranties,preferences.view]);
  useEffect(()=>{setStatuses([]);setPage(1)},[templateId]);
  const activeTemplate=templates.find(template=>template.id===templateId)||templates[0];
  const kanbanColumns:KanbanColumn[]=(activeTemplate?.columns||[]).map(column=>({key:column.id,label:column.name,statuses:[column.id],color:column.color}));
  const modelColumnForRow=(row:Maintenance)=>resolveKanbanModelColumn(activeTemplate,row,maintenanceStatusAliases);
  const columnForRow=(row:Maintenance)=>modelColumnForRow(row)?.id||"";
  const filtered = rows.filter(
    (row) =>
      JSON.stringify(row)
        .toLocaleLowerCase("pt-BR")
        .includes(query.toLocaleLowerCase("pt-BR")) &&
      (!statuses.length || statuses.includes(columnForRow(row))) &&
      (!priorities.length || priorities.includes(row.priority)) &&
      (!warranties.length || warranties.includes(row.warranty_status)),
  );
  const pageSize = 10;
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visibleRows = preferences.view === "kanban"
    ? filtered
    : filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage((current) => Math.min(current, pages)); }, [pages]);
  return (
    <section
      className={`card data-table-card maintenance-table ${!filtered.length && preferences.view === "table" ? "empty-table-card" : ""} ${preferences.className}`}
    >
      <div className="toolbar table-toolbar">
        <div className="table-filter-group">
          <label className="search">
            <Search />
            <input
              placeholder="Buscar por código, cliente ou descrição"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <MultiSelectFilter label="Todos os status" options={(activeTemplate?.columns||[]).map(column=>({value:column.id,label:column.name}))} value={statuses} onChange={setStatuses}/>
          <MultiSelectFilter label="Todas as prioridades" options={Object.entries(priorityLabels).map(([value,label])=>({value,label}))} value={priorities} onChange={setPriorities}/>
          <MultiSelectFilter label="Todas as garantias" options={Object.entries(warrantyLabels).map(([value,label])=>({value,label}))} value={warranties} onChange={setWarranties}/>
          {templates.length>1&&<label className="kanban-template-select"><span>Modelo</span><select value={activeTemplate?.id||""} onChange={event=>setTemplateId(event.target.value)}>{templates.map(template=><option key={template.id} value={template.id}>{template.name}</option>)}</select></label>}
        </div>
        <div className="table-toolbar-actions">
          <TableDisplayControls
            count={filtered.length}
            total={rows.length}
            columns={columns}
            hidden={preferences.hidden}
            view={preferences.view}
            onView={preferences.setView}
            onToggle={preferences.toggle}
            onReset={preferences.reset}
          />
          <button className="button primary" onClick={onAdd}><Plus />Novo PM</button>
        </div>
      </div>
      {preferences.view === "kanban" ? (
        <KanbanBoard
          columns={kanbanColumns}
          rows={visibleRows}
          getStatus={columnForRow}
          onOpen={(row) => onOpen(row.id)}
          onStatusChange={async(row,columnId,comment)=>{try{await api.changeMaintenanceKanbanStatus(row.id,columnId,comment);setRows(await api.list())}catch(error:any){onError(error.message);throw error}}}
          renderCard={(row) => {
            const days=maintenanceLeadTimeDays(row.request_date,row.created_at,row.lead_time_stopped_at||row.completed_at||(row.status==="cancelled"?row.updated_at:undefined));
            return <>
              <strong>{formatReportUppercaseText(row.customer_name) || "Cliente não informado"}</strong>
              <div className="kanban-card-footer"><small>{row.code}</small><span className={`lead-time lead-time-${leadTimeTone(days)}`} title={`Lead time: ${days} dia${days===1?"":"s"}`}><Clock3/>{days}d</span></div>
            </>;
          }}
        />
      ) : (
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th data-column="code">Código</th>
              <th data-column="customer">Cliente</th>
              <th data-column="description">Descrição</th>
              <th data-column="items">Itens</th>
              <th data-column="status">Status</th>
              <th data-column="priority">Prioridade</th>
              <th data-column="warranty">Garantia</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const modelColumn=modelColumnForRow(row);
              return (
              <tr
                key={row.id}
                className="clickable-row"
                tabIndex={0}
                onClick={() => onOpen(row.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen(row.id);
                  }
                }}
                aria-label={`Abrir ${row.code} para edição`}
              >
                <td data-column="code" className="mono">
                  {row.code}
                </td>
                <td data-column="customer">
                  {formatReportUppercaseText(row.customer_name) || "—"}
                </td>
                <td data-column="description">
                  <b>{formatReportUppercaseText(row.description) || "Sem descrição informada"}</b>
                </td>
                <td data-column="items">
                  <span className="item-count">
                    {row.item_count || 0}{" "}
                    {row.item_count === 1 ? "item" : "itens"}
                  </span>
                  <small className="table-secondary">{row.main_product||"Sem produto informado"}</small>
                </td>
                <td data-column="status">
                  <span className="status-pill" style={{borderColor:modelColumn?.color||"",color:modelColumn?.color||"",backgroundColor:modelColumn?.color?`color-mix(in srgb,${modelColumn.color} 12%,transparent)`:undefined}}>
                    {modelColumn?.name || "Sem etapa configurada"}
                  </span>
                </td>
                <td data-column="priority">
                  {priorityLabels[row.priority] || "Não informada"}
                </td>
                <td data-column="warranty" title={row.warranty_expires_at?`Válida até ${new Date(`${row.warranty_expires_at}T12:00:00`).toLocaleDateString("pt-BR")}`:"Data de validade não informada"}>
                  {warrantyLabels[row.warranty_status] || "Em análise"}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      {!filtered.length && (
        <div className="empty">
          <Wrench />
          <h3>Nenhum PM encontrado</h3>
          <p>Crie um pedido ou ajuste a busca.</p>
        </div>
      )}
      {preferences.view === "table" && <div className="pagination maintenance-pagination">
        <button disabled={page === 1} onClick={() => setPage((current) => current - 1)}>Anterior</button>
        <span>Página {page} de {pages}</span>
        <button disabled={page === pages} onClick={() => setPage((current) => current + 1)}>Próxima</button>
      </div>}
    </section>
  );
}
